<template>
  <SrpDropdown class="location-category-dropdown" placement="bottom-start" :offset="4" :width="190">
    <template #clickableElement="{ isDropdownVisible }">
      <button type="button" class="location-category-dropdown__trigger" :aria-label="`Filter by place type: ${selectedOption?.label || props.allLabel}`" :aria-expanded="isDropdownVisible">
        <LocationCategoryIcon v-if="selectedOption" :option="selectedOption" :size="24" />
        <span v-else class="location-category-dropdown__all-icon"><IconEmbedded name="grid_2" :size="16" color="currentColor" /></span>
        <span class="location-category-dropdown__label">{{ selectedOption?.label || props.allLabel }}</span>
        <IconEmbedded name="caret-bottom_3" :size="14" color="#667085" />
      </button>
    </template>
    <template #dropdown="{ close, isDropdownVisible }">
      <div class="location-category-dropdown__menu" :inert="!isDropdownVisible" :aria-hidden="!isDropdownVisible" @keydown.esc.stop="close()">
        <button type="button" :class="{ 'location-category-dropdown__choice--selected': !props.modelValue }" @click="choose('', close)">
          <span class="location-category-dropdown__all-icon"><IconEmbedded name="grid_2" :size="16" color="currentColor" /></span>{{ props.allLabel }}
        </button>
        <button v-for="option in options" :key="option.value" type="button" :class="{ 'location-category-dropdown__choice--selected': props.modelValue === option.value }" @click="choose(option.value, close)">
          <LocationCategoryIcon :option="option" :size="24" />{{ option.label }}
        </button>
      </div>
    </template>
  </SrpDropdown>
</template>

<script setup lang="ts">
import { computed } from "vue";
import SrpDropdown from "@components/ui/SrpDropdown.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import LocationCategoryIcon from "./LocationCategoryIcon.vue";
import { EVENT_CATEGORY_OPTION, LOCATION_CATEGORY_OPTIONS, PERSON_CATEGORY_OPTION } from "@logic/LocationCategories";

const props = withDefaults(
  defineProps<{
    /** A category value, "Event", or "" for all types. */
    modelValue: string;
    includeEvent?: boolean;
    includePeople?: boolean;
    /** Label for the "no filter" choice. */
    allLabel?: string;
  }>(),
  { includeEvent: true, includePeople: false, allLabel: "All types" }
);
const emit = defineEmits<{ (e: "update:modelValue", value: string): void }>();

const options = computed(() => [
  ...LOCATION_CATEGORY_OPTIONS,
  ...(props.includeEvent ? [EVENT_CATEGORY_OPTION] : []),
  ...(props.includePeople ? [PERSON_CATEGORY_OPTION] : []),
]);
const selectedOption = computed(() => options.value.find(option => option.value === props.modelValue));

function choose(value: string, close: () => void) {
  emit("update:modelValue", value);
  close();
}
</script>

<style scoped>
.location-category-dropdown { width: 100%; }
.location-category-dropdown :deep(.dropdown-wrapper__clickable-element) { width: 100%; }
.location-category-dropdown__trigger { display: flex; align-items: center; gap: 7px; width: 100%; height: 36px; padding: 0 9px; border: 1px solid #d8dce2; border-radius: 6px; background: white; color: #28343b; cursor: pointer; text-align: left; white-space: nowrap; }
.location-category-dropdown__label { flex: 1; overflow: hidden; text-overflow: ellipsis; }
.location-category-dropdown__trigger:hover { border-color: #087f82; }
.location-category-dropdown__trigger:focus-visible { outline: 2px solid #087f82; outline-offset: 2px; }
.location-category-dropdown__menu { padding: 5px; }
.location-category-dropdown__all-icon { display: inline-flex; align-items: center; justify-content: center; width: 24px; height: 24px; flex: 0 0 24px; border-radius: 50%; color: #45566b; background: #e9eef5; }
.location-category-dropdown__menu button { display: flex; align-items: center; gap: 10px; width: 100%; padding: 8px 10px; border: 0; border-radius: 4px; background: white; color: #28343b; cursor: pointer; text-align: left; white-space: nowrap; }
.location-category-dropdown__menu button:hover, .location-category-dropdown__menu .location-category-dropdown__choice--selected { background: #eef6f5; }
.location-category-dropdown__menu button:focus-visible { outline: 2px solid #087f82; outline-offset: -2px; }
</style>
